import { SupabaseAuthUtils } from "./SupabaseAuthUtils";
import { supabase } from "@/integrations/supabase/supabaseClient";

/**
 * Common subscription query utilities for Supabase repositories
 */
export class SubscriptionQueryUtils {
  /**
   * Get all subscription IDs for the current user
   * @returns {Promise<string[]>} Array of subscription IDs
   * @throws {Error} If user is not authenticated or query fails
   */
  static async getUserSubscriptionIds(): Promise<string[]> {
    const userId = await SupabaseAuthUtils.getCurrentUserId();

    const { data: userSubscriptions, error } = await supabase
      .from("subscriptions")
      .select("id")
      .eq("user_id", userId);

    if (error) {
      throw error;
    }

    return userSubscriptions.map((sub) => sub.id);
  }

  /**
   * Get subscription details by ID
   * @param subscriptionId - The subscription ID to fetch
   * @returns {Promise<any>} Subscription data
   * @throws {Error} If subscription not found or query fails
   */
  static async getSubscriptionById(subscriptionId: string) {
    const { data: subscription, error } = await supabase
      .from("subscriptions")
      .select("id, billing_cycle, billing_type")
      .eq("id", subscriptionId)
      .single();

    if (error || !subscription) {
      throw new Error(
        `Failed to fetch subscription: ${error?.message || "Not found"}`,
      );
    }

    return subscription;
  }

  /**
   * Check if subscription exists and belongs to current user
   * @param subscriptionId - The subscription ID to validate
   * @returns {Promise<boolean>} True if subscription exists and belongs to user
   */
  static async validateUserOwnsSubscription(
    subscriptionId: string,
  ): Promise<boolean> {
    try {
      const userId = await SupabaseAuthUtils.getCurrentUserId();

      const { data, error } = await supabase
        .from("subscriptions")
        .select("id")
        .eq("id", subscriptionId)
        .eq("user_id", userId)
        .single();

      return !error && !!data;
    } catch {
      return false;
    }
  }

  /**
   * Update subscription's next payment date
   * @param subscriptionId - The subscription ID to update
   * @param nextPaymentDate - The new next payment date (ISO string)
   * @returns {Promise<void>}
   * @throws {Error} If update fails
   */
  static async updateNextPaymentDate(
    subscriptionId: string,
    nextPaymentDate: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("subscriptions")
      .update({ next_payment_date: nextPaymentDate })
      .eq("id", subscriptionId);

    if (error) {
      throw new Error(`Failed to update next_payment_date: ${error.message}`);
    }
  }

  /**
   * Get subscription IDs that belong to the current user (safe version)
   * @returns {Promise<string[]>} Array of subscription IDs, empty if none found
   */
  static async getUserSubscriptionIdsSafe(): Promise<string[]> {
    try {
      return await this.getUserSubscriptionIds();
    } catch {
      return [];
    }
  }
}
